import { checkPermissions } from "@/base/check-permissions"
import type { FarmUserPermissionName } from "@/constants/permissions"

import { denyPermission } from "@/redux/actions"
import { useRootDispatch } from "@/redux/useRootDispatch"
import { useShallowEqualSelector } from "@/redux/useRootSelector"
import { getFarmUserPermissionsFromState } from "@/redux/user-permissions.reducer"
import i18n from "@/translations/i18n"
import { Alert, Platform } from "react-native"

export type WithPermissions = <TArgs extends unknown[]>(params: {
  callback: (...args: TArgs) => unknown
  required: FarmUserPermissionName | FarmUserPermissionName[]
}) => (...args: TArgs) => unknown
export function usePermissionCheckedAction() {
  const dispatch = useRootDispatch()
  const provided = useShallowEqualSelector(getFarmUserPermissionsFromState)

  const message = i18n.t("farmUserPermissions:youDoNotHavePermission")
  const title = i18n.t("farmUserPermissions:permissionDenied")

  const withPermissions: WithPermissions = ({ callback, required }) => {
    const check = checkPermissions({ provided, required })
    return (...args) => {
      // DEBUG: Log permission check
      // eslint-disable-next-line no-console
      console.warn("[Permission] Checking", { check, provided, required })
      // If any permissions are missing, the user cannot perform the action
      // TODO: Does this work for demo mode?
      if (
        check.code === "MISSING_PERMISSIONS" &&
        check.missingPermissions.length > 0
      ) {
        // eslint-disable-next-line no-console
        console.warn("[Permission] DENIED", { missingPermissions: check.missingPermissions })
        if (Platform.OS === "web") {
          return dispatch(denyPermission(check))
        }
        return Alert.alert(title, message)
      }
      // eslint-disable-next-line no-console
      console.warn("[Permission] ALLOWED, calling callback")
      // Let the action proceed, forwarding any arguments
      return callback(...args)
    }
  }

  return withPermissions
}
